import React from 'react';
import { Layout } from '../components/common/Layout';
import { Link } from 'react-router-dom';
import { Input } from '../components/common/Input';
import { SEOHead } from '../components/seo/SEOHead';
import {
  BadgeCheck,
  BarChart,
  BookOpenCheck,
  HeartHandshake,
  Link as LinkIcon,
  MessageSquarePlus,
  Sparkle,
  Users,
} from 'lucide-react';

// Helper components for the sections to keep the main page clean
const HeroSection: React.FC = () => (
  <div className="bg-gradient-to-r from-cyan-400 to-blue-500 text-white py-20 px-4 sm:px-6 lg:px-8">
    <div className="max-w-7xl mx-auto text-center">
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mb-4">
        Earn Recurring Income by Helping Everyday People Learn AI
      </h1>
      <p className="text-xl sm:text-2xl mb-10">
        Join the trAIned up affiliate program and earn monthly commission for the full lifetime of every member you bring in.
      </p>
      <div className="flex justify-center space-x-4">
        <Link
          to="/affiliateregistration"
          className="bg-white text-blue-600 hover:bg-gray-100 font-bold py-3 px-8 rounded-lg shadow-lg transition duration-300 ease-in-out text-lg"
        >
          Register as affiliate
        </Link>
        <Link
          to="/dashboard/affiliate"
          className="bg-blue-600 text-white hover:bg-blue-700 font-bold py-3 px-8 rounded-lg shadow-lg transition duration-300 ease-in-out text-lg"
        >
          Log in to dashboard
        </Link>
      </div>
    </div>
  </div>
);

const WhyPromoteSection: React.FC = () => (
  <div className="bg-green-50 py-20 px-4 sm:px-6 lg:px-8">
    <div className="max-w-7xl mx-auto">
      <h2 className="text-3xl font-bold text-center mb-12 text-gray-800">
        Why Promote trAInedup.ai?
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {/* Card 1: Recurring Monthly Commission */}
        <div className="bg-white p-6 rounded-xl shadow-lg border-t-4 border-green-500">
          <h3 className="text-xl font-semibold mb-3 text-green-600">Recurring Monthly Commission</h3>
          <p className="text-gray-600">
            Earn 30% commission every month for the lifetime of each subscription that you refer
          </p>
        </div>
        {/* Card 2: Easy to Promote */}
        <div className="bg-white p-6 rounded-xl shadow-lg border-t-4 border-green-500">
          <h3 className="text-xl font-semibold mb-3 text-green-600">Easy to Promote</h3>
          <p className="text-gray-600">
            Affordable monthly pricing with practical courses and supportive community that appeals to everyday people
          </p>
        </div>
        {/* Card 3: High Retention */}
        <div className="bg-white p-6 rounded-xl shadow-lg border-t-4 border-green-500">
          <h3 className="text-xl font-semibold mb-3 text-green-600">High Retention</h3>
          <p className="text-gray-600">
            Members receive a personal learning plan, fresh content each month and regular support. This creates long term value and encourages them to stay subscribed
          </p>
        </div>
      </div>
    </div>
  </div>
);

const HowItWorksSection: React.FC = () => {
  const steps = [
    { id: 1, title: "User clicks your referral link", description: "The user arrives on the trAInedup.ai referral landing page and creates their account" },
    { id: 2, title: "Takes their Skills Assessment", description: "The user completes their Skills Assessment and is provided with their learning plan" },
    { id: 3, title: "Registers for membership", description: "The user upgrades to a paid membership, paying their first monthly payment" },
    { id: 4, title: "7-day refund period passes", description: "The user keeps their membership past the 7-day refund period" },
    { id: 5, title: "Affiliate payment processed", description: "The affiliate payment is processed within 14 days of registration" },
  ];

  const circleColors = ['bg-blue-500', 'bg-emerald-500', 'bg-indigo-500', 'bg-cyan-500', 'bg-amber-500'];

  return (
    <div className="py-20 px-4 sm:px-6 lg:px-8 bg-sky-50">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl font-bold text-center mb-12 text-gray-800">
          How the Membership Works
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
          {steps.map((step, index) => (
            <div key={step.id} className="flex flex-col items-center text-center space-y-3">
              <div
                className={`w-12 h-12 flex items-center justify-center rounded-full text-white font-bold ${circleColors[index % circleColors.length]}`}
              >
                {step.id}
              </div>
              <h4 className="font-semibold text-gray-800">{step.title}</h4>
              <p className="text-sm text-gray-600">{step.description}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const ContactSection: React.FC = () => {
  const [subject, setSubject] = React.useState('');
  const [message, setMessage] = React.useState('');

  const handleSendEmail = () => {
    const mailtoLink = `mailto:info@trainedup.ai?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(message)}`;
    window.location.href = mailtoLink;
  };

  return (
    <div id="affiliate-contact" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-3xl mx-auto">
        <h2 className="text-3xl font-bold text-center mb-8 text-gray-800">Send Us a Message</h2>
        <div className="space-y-4">
          <Input
            label="Subject"
            type="text"
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
            fullWidth
          />
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2" htmlFor="affiliate-message">
              Message
            </label>
            <textarea
              id="affiliate-message"
              className="w-full min-h-[160px] border border-neutral-300 rounded-lg p-4 text-neutral-800 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              value={message}
              onChange={(event) => setMessage(event.target.value)}
            />
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleSendEmail}
              className="bg-blue-600 text-white hover:bg-blue-700 font-bold py-3 px-6 rounded-lg shadow-lg transition duration-300 ease-in-out"
            >
              Email info@trainedup.ai
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const PrinciplesSection: React.FC = () => {
  const principles = [
    {
      title: 'Approachable',
      description: 'We speak like users are talking to a non-technical friend. No jargon, just clear, warm communication.',
      icon: MessageSquarePlus,
      accent: 'bg-rose-50 text-rose-500',
    },
    {
      title: 'Supportive',
      description: 'We understand users uncertainty and give them confidence at every step in their learning journey.',
      icon: HeartHandshake,
      accent: 'bg-sky-50 text-sky-500',
    },
    {
      title: 'Empowering',
      description: 'Every small win is celebrated. Each win becomes fuel for their continued growth.',
      icon: Sparkle,
      accent: 'bg-amber-50 text-amber-500',
    },
    {
      title: 'Practical',
      description: 'Everything we teach is useful and actionable. Users can apply what they have learned straight away.',
      icon: BookOpenCheck,
      accent: 'bg-emerald-50 text-emerald-500',
    },
  ];

  return (
    <div className="py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl font-bold text-center mb-12 text-gray-800">trAInedup.ai Principles</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {principles.map((principle) => (
            <div key={principle.title} className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 h-full">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${principle.accent} mb-4`}>{
                <principle.icon className="h-6 w-6" />
              }</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{principle.title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{principle.description}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const UniqueApproachSection: React.FC = () => {
  const approaches = [
    {
      title: 'Personalised Assessment',
      description:
        "We start by understanding who the user is, what their current knowledge is and what their future goals are. No two learning paths are the same.",
    },
    {
      title: 'Bite-Sized Learning',
      description:
        'Our courses are designed to fit around busy schedules. Users can learn a skill that they can apply in 15-30 minute sessions.',
    },
    {
      title: 'Community Support',
      description:
        'Users get feedback and support from our community. They can join community calls with others and members of the trAIned up.ai team for guidance.',
    },
  ];

  return (
    <div className="bg-cyan-50 py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl font-bold text-center mb-12 text-gray-800">Our Unique Approach</h2>
        <p className="text-center text-gray-700 max-w-3xl mx-auto mb-10">
          We have designed our platform around how busy people actually learn.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {approaches.map((approach, index) => (
            <div key={approach.title} className="bg-white rounded-2xl shadow-lg border border-cyan-100 p-8 h-full">
              <div className="w-12 h-12 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-lg mb-4">
                {index + 1}
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">{approach.title}</h3>
              <p className="text-gray-600 leading-relaxed text-sm">{approach.description}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const IdealForSection: React.FC = () => {
  const audienceHighlights = [
    {
      title: 'Creators with a niche following',
      description: 'Hands-on or creative fields like candle making, DIY, and hobbyist communities.',
      icon: Sparkle,
      accent: 'bg-cyan-100 text-cyan-600',
    },
    {
      title: 'Online teachers',
      description: 'Educators sharing resources, tips, and support with their fellow teachers or students.',
      icon: BookOpenCheck,
      accent: 'bg-amber-100 text-amber-600',
    },
    {
      title: 'AI influencers and curators',
      description: 'People who showcase AI tools, tutorials, or trend breakdowns for their followers.',
      icon: BadgeCheck,
      accent: 'bg-indigo-100 text-indigo-600',
    },
    {
      title: 'Tech and productivity creators',
      description: 'Anyone helping people work smarter with tutorials, workflows, and practical tips.',
      icon: BarChart,
      accent: 'bg-emerald-100 text-emerald-600',
    },
    {
      title: 'Small business owners',
      description: 'Entrepreneurs who want to guide fellow business owners with actionable AI advice.',
      icon: HeartHandshake,
      accent: 'bg-rose-100 text-rose-600',
    },
    {
      title: 'Coaches, consultants, and freelancers',
      description: 'Professionals recommending proven tools and resources to the clients they support.',
      icon: Users,
      accent: 'bg-blue-100 text-blue-600',
    },
    {
      title: 'Students and emerging creators',
      description: 'People building a community who want to share valuable, future-ready skills.',
      icon: MessageSquarePlus,
      accent: 'bg-purple-100 text-purple-600',
    },
    {
      title: 'Anyone who shares helpful content',
      description: 'If your audience trusts you for practical advice, this program gives them useful next steps.',
      icon: HeartHandshake,
      accent: 'bg-slate-100 text-slate-600',
    },
  ];

  return (
    <div className="bg-gray-50 py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto text-center">
        <div className="space-y-4 max-w-3xl mx-auto mb-10">
          <h2 className="text-3xl font-bold text-gray-800">Who This Program Is Ideal For</h2>
          <p className="text-gray-700 text-lg">
            This program is for anyone with an audience that would benefit from simple, practical AI training.
            <span className="block mt-2">Which may include:</span>
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 auto-rows-fr items-stretch">
          {audienceHighlights.map((audience) => (
            <div key={audience.title} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 text-left h-full flex flex-col">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${audience.accent} mb-4`}>
                <audience.icon className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{audience.title}</h3>
              <p className="text-gray-600 leading-relaxed text-sm">{audience.description}</p>
            </div>
          ))}
        </div>
        <p className="text-gray-700 text-lg mt-10 max-w-3xl mx-auto">
          If your audience trusts you for practical advice, this program gives you something genuinely helpful, simple to explain, and easy to share.
        </p>
      </div>
    </div>
  );
};

const HowToJoinSection: React.FC = () => {
  const steps = [
    {
      title: 'Register for free',
      icon: BadgeCheck,
      description: 'Sign up as an affiliate for free and access your dashboard.',
    },
    {
      title: 'Copy your link',
      icon: LinkIcon,
      description: 'Copy your unique referral link from your dashboard to share with your audience.',
    },
    {
      title: 'Share with your audience',
      icon: Users,
      description: 'Promote trAInedup.au to your followers and communities who could benefit from joining.',
    },
    {
      title: 'Track sign ups and earnings',
      icon: BarChart,
      description: 'Monitor referrals and commissions easily inside your dashboard.',
    },
    {
      title: 'Receive your payments',
      icon: HeartHandshake,
      description: 'Get paid within 14 days of any sign ups, after the refund period passes.',
    },
  ];

  return (
    <div className="bg-blue-50 py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl font-bold text-center mb-12 text-gray-800">How to Join</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
          {steps.map((step) => (
            <div key={step.title} className="bg-white rounded-2xl shadow-sm border border-blue-100 p-6 text-center h-full">
              <div className="w-12 h-12 rounded-full bg-white text-blue-600 border border-blue-200 flex items-center justify-center mx-auto mb-4">
                <step.icon className="h-6 w-6" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{step.title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{step.description}</p>
            </div>
          ))}
        </div>
        <div className="flex justify-center mt-10">
          <Link
            to="/affiliateregistration"
            className="bg-blue-600 text-white hover:bg-blue-700 font-bold py-3 px-8 rounded-lg shadow-lg transition duration-300 ease-in-out text-lg"
          >
            Register as affiliate
          </Link>
        </div>
      </div>
    </div>
  );
};

export const AffiliateLandingPage: React.FC = () => {
  return (
    <Layout showFooter={true} showHeader={false} showUpgradeBanner={false} showChatbot={false}>
      <SEOHead
        title="Affiliate Program - Earn Recurring Income | trAInedup.ai"
        description="Join the trAInedup.ai affiliate program and earn 30% recurring monthly commission for the lifetime of every member you refer. Easy to promote, high retention."
        canonical="https://trainedup.ai/affiliate"
        keywords={['AI affiliate program', 'recurring commission', 'AI education affiliate', 'earn money promoting AI courses']}
      />
      <HeroSection />
      <WhyPromoteSection />
      <HowItWorksSection />
      <PrinciplesSection />
      <UniqueApproachSection />
      <IdealForSection />
      <HowToJoinSection />
      <ContactSection />
    </Layout>
  );
};
